import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "../constants/services";
import { ClientProps } from "../pages/notifications/DigitalSchedule/ClientSchedule";
import { IFleetStatus } from "../pages/vehicleHealth/components/interface/IFleetStatus";
import { IHistoryStaus } from "../pages/vehicleHealth/components/interface/IHistoryStatus";

export const vehicleStatusApi = createApi({
	reducerPath: "vehicleStatusApi",
	baseQuery: dynamicBaseQuery,
	endpoints: (builder) => ({
		getOperativeCards: builder.mutation<any, any>({
			query: (requestPayload) => ({
				url: "vehicleStatus/operativeCard",
				method: "POST",
				body: requestPayload,
				responseHandler: async (response: Response) => {
					const data = await response.json();
					return data;
				},
			}),
		}),
		getFaultCodesCard: builder.mutation<any, any>({
			query: (requestPayload) => ({
				url: "vehicleStatus/faultCodesCard",
				method: "POST",
				body: requestPayload,
				responseHandler: async (response: Response) => {
					const data = await response.json();
					return data;
				},
			}),
		}),
		getMaintenanceCard: builder.mutation<any, any>({
			query: (requestPayload) => ({
				url: "vehicleStatus/maintenanceCard",
				method: "POST",
				body: requestPayload,
				responseHandler: async (response: Response) => {
					const data = await response.json();
					return data.scores;
				},
			}),
		}),
		getVehicleStatus: builder.query<any, any>({
			query: (requestPayload) => {
				const { version, ...body } = requestPayload;
				const url = version ? `vehicleStatus?version=${version}` : "vehicleStatus";
				return {
					url,
					method: "POST",
					body,
					responseHandler: async (response: Response) => {
						const data = await response.json();
						return data;
					},
				};
			},
			// Cache por 5 minutos para permitir persistência ao dar F5
			keepUnusedDataFor: 300,
		}),
		getVehicleDiagnostic: builder.query<any, any>({
			query: (id) => ({
				url: `vehicleStatus/diagnosticData?assetId=${id}`,
				responseHandler: async (response: Response) => {
					const data = await response.json();
					return data;
				},
			}),
		}),
		// Nova query para buscar dados completos de um veículo específico
		getVehicleById: builder.query<any, { assetId: string; startAt: string; endAt: string }>({
			query: ({ assetId, startAt, endAt }) => ({
				url: "vehicleStatus",
				method: "POST",
				body: {
					filterType: "ALL_WITH_COUNTERS",
					pageSize: 1,
					startAt,
					endAt,
					assetIds: [assetId],
					tagIds: [],
					order: [],
					page: 0,
				},
				responseHandler: async (response: Response) => {
					const data = await response.json();
					// Retorna apenas o primeiro item (o veículo específico)
					return data.data?.[0] || null;
				},
			}),
			// Cache por 2 minutos
			keepUnusedDataFor: 120,
		}),
		// Nova query para buscar o historico do veículo
		getVehicleHeaderStatus: builder.query<any, { assetId: string }>({
			query: (assetId) => ({
				url: `api/v1/vehicle-health/${assetId}/status`,
				method: "GET",
				responseHandler: async (response: Response) => {
					const data = await response.json();
					return data;
				},
			}),
		}),
		// Nova query para buscar resumo da frota status do veículo
		getVehicleHeaderDashboard: builder.query<any, any>({
			query: () => ({
				url: `api/v1/vehicle-health/dashboard`,
				method: "GET",
				responseHandler: async (response: Response) => {
					const data = await response.json();
					return data;
				},
			}),
		}),
		// Nova query para buscar criteria
		getVehicleCriteriaId: builder.query<any, { uuid: string }>({
			query: (uuid) => ({
				url: `api/v1/vehicle-health/criteria/${uuid}`,
				method: "GET",
				responseHandler: async (response: Response) => {
					const data = await response.json();
					return data;
				},
			}),
		}),
		getVehicleCriteria: builder.query<any, any>({
			query: (uuid) => ({
				url: `api/v1/vehicle-health/criteria`,
				method: "GET",
				responseHandler: async (response: Response) => {
					const data = await response.json();
					return data;
				},
			}),
		}),
		// Nova query para buscar histórico de mudança
		getVehicleHistoryId: builder.query<IHistoryStaus[], { assetId: string }>({
			query: (assetId) => ({
				url: `api/v1/vehicle-health/assets/${assetId}/history`,
				method: "GET",
				responseHandler: async (response: Response) => {
					const data: [] = await response.json();
					return data;
				},
			}),
		}),

		//NOVA REQUEST PARA FILTRO STATUS
		getVehicleFleetStatus: builder.query<any, { tagIds: string[]; assetIds?: string[] }>({
			query: (requestPayload) => ({
				url: "api/v1/vehicle-health/summary",
				method: "POST",
				body: {
					tagIds: requestPayload.tagIds,
					assetIds: requestPayload.assetIds || [],
				},
				responseHandler: async (response: Response) => {
					const data = await response.json();
					return data;
				},
			}),
			// Cache por 5 minutos para permitir persistência ao dar F5
			keepUnusedDataFor: 300,
		}),
	}),
});

export const {
	useGetMaintenanceCardMutation,
	useGetVehicleStatusQuery,
	useLazyGetVehicleStatusQuery,
	useGetFaultCodesCardMutation,
	useGetOperativeCardsMutation,
	useGetVehicleDiagnosticQuery,
	useGetVehicleByIdQuery,
	useGetVehicleHeaderStatusQuery,
	useGetVehicleHeaderDashboardQuery,
	useGetVehicleCriteriaIdQuery,
	useGetVehicleHistoryIdQuery,
	useGetVehicleFleetStatusQuery,
	useGetVehicleCriteriaQuery,
} = vehicleStatusApi;
